import { useCallback, useReducer } from 'react'
import { IHsLocalTariff } from '../../types'

interface LocalTariffState {
  tariffs: IHsLocalTariff[]
  selectedIndex: number
  modalStates: {
    create: boolean
    edit: boolean
    view: boolean
  }
}

type LocalTariffAction =
  | { type: 'SET_TARIFFS'; payload: IHsLocalTariff[] }
  | { type: 'ADD_TARIFF'; payload: IHsLocalTariff }
  | { type: 'UPDATE_TARIFF'; payload: { index: number; tariff: IHsLocalTariff } }
  | { type: 'SET_SELECTED_INDEX'; payload: number }
  | { type: 'OPEN_MODAL'; payload: keyof LocalTariffState['modalStates'] }
  | { type: 'CLOSE_MODAL'; payload: keyof LocalTariffState['modalStates'] }
  | { type: 'RESET_SELECTION' }

const initialState: LocalTariffState = {
  tariffs: [],
  selectedIndex: -1,
  modalStates: {
    create: false,
    edit: false,
    view: false,
  },
}

function localTariffReducer(state: LocalTariffState, action: LocalTariffAction): LocalTariffState {
  switch (action.type) {
    case 'SET_TARIFFS':
      return { ...state, tariffs: action.payload }
    case 'ADD_TARIFF':
      return { ...state, tariffs: [...state.tariffs, action.payload] }
    case 'UPDATE_TARIFF':
      return {
        ...state,
        tariffs: state.tariffs.map((tariff, index) =>
          index === action.payload.index ? action.payload.tariff : tariff
        ),
      }
    case 'SET_SELECTED_INDEX':
      return { ...state, selectedIndex: action.payload }
    case 'OPEN_MODAL':
      return {
        ...state,
        modalStates: { ...state.modalStates, [action.payload]: true },
      }
    case 'CLOSE_MODAL':
      return {
        ...state,
        modalStates: { ...state.modalStates, [action.payload]: false },
      }
    case 'RESET_SELECTION':
      return { ...state, selectedIndex: -1 }
    default:
      return state
  }
}

interface UseLocalTariffManagerReturn {
  state: LocalTariffState
  actions: {
    setTariffs: (tariffs: IHsLocalTariff[]) => void
    addTariff: (tariff: IHsLocalTariff) => void
    updateTariff: (index: number, tariff: IHsLocalTariff) => void
    setSelectedIndex: (index: number) => void
    openModal: (modal: keyof LocalTariffState['modalStates']) => void
    closeModal: (modal: keyof LocalTariffState['modalStates']) => void
    resetSelection: () => void
    getSelectedTariff: () => IHsLocalTariff | undefined
  }
}

export const useLocalTariffManager = (
  initialTariffs: IHsLocalTariff[] = []
): UseLocalTariffManagerReturn => {
  const [state, dispatch] = useReducer(localTariffReducer, {
    ...initialState,
    tariffs: initialTariffs,
  })

  const setTariffs = useCallback((tariffs: IHsLocalTariff[]) => {
    dispatch({ type: 'SET_TARIFFS', payload: tariffs })
  }, [])

  const addTariff = useCallback((tariff: IHsLocalTariff) => {
    dispatch({ type: 'ADD_TARIFF', payload: tariff })
  }, [])

  const updateTariff = useCallback((index: number, tariff: IHsLocalTariff) => {
    dispatch({ type: 'UPDATE_TARIFF', payload: { index, tariff } })
  }, [])

  const setSelectedIndex = useCallback((index: number) => {
    dispatch({ type: 'SET_SELECTED_INDEX', payload: index })
  }, [])

  const openModal = useCallback((modal: keyof LocalTariffState['modalStates']) => {
    dispatch({ type: 'OPEN_MODAL', payload: modal })
  }, [])

  const closeModal = useCallback((modal: keyof LocalTariffState['modalStates']) => {
    dispatch({ type: 'CLOSE_MODAL', payload: modal })
  }, [])

  const resetSelection = useCallback(() => {
    dispatch({ type: 'RESET_SELECTION' })
  }, [])

  const getSelectedTariff = useCallback((): IHsLocalTariff | undefined => {
    if (state.selectedIndex >= 0 && state.selectedIndex < state.tariffs.length) {
      return state.tariffs[state.selectedIndex]
    }
    return undefined
  }, [state.selectedIndex, state.tariffs])

  return {
    state,
    actions: {
      setTariffs,
      addTariff,
      updateTariff,
      setSelectedIndex,
      openModal,
      closeModal,
      resetSelection,
      getSelectedTariff,
    },
  }
}
